import React, { useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import { ColorRing } from 'react-loader-spinner';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import CustomTooltip from './CustomTooltip';

import { Device, HistoryTrigger } from 'api/InteractiveMap/types';
import { useTrapDeleteModalStore } from 'components/DeleteTrapModal/TrapsDeleteModal';
import { StatusTypes } from 'api/traps/types';

interface STTrapPopupProps {
  trap: Device;
  onView?: () => void;
  onMove?: () => void;
  showMoveActions?: boolean;
  onMoveAction?: (action: 'adjustment' | 'new_place', caught_pests?: number | null) => void;
  isMoving?: boolean;
  isLoading?: boolean;
  trapType: 'smst' | 'srst';
  isActiveFilterArchive?: boolean;
}

export const STTrapPopup: React.FC<STTrapPopupProps> = ({
  trap,
  onMove,
  showMoveActions,
  onMoveAction,
  isLoading,
  trapType,
  isActiveFilterArchive,
}) => {
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataForTooltip, setDataForTooltip] = useState<HistoryTrigger[]>([]);
  const [isCapture, setIsCapture] = useState<boolean>(false);
  const { openDeleteTrapModal } = useTrapDeleteModalStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openModal = (showCapture: boolean) => {
    if (isModalOpen) return;

    setIsCapture(showCapture);
    setDataForTooltip(
      trap.combine_history_triggers
        ?.sort((a, b) => dayjs(b.date_time).unix() - dayjs(a.date_time).unix())
        .filter(activity =>
          showCapture
            ? activity.status === StatusTypes.APPROVED_MICE || activity.status === StatusTypes.APPROVED_NOT_MICE
            : activity.status === StatusTypes.CAUGHT_EMPTY,
        ) ?? [],
    );
    setIsModalOpen(true);
  };

  const onClose = () => {
    setDataForTooltip([]);
    setIsModalOpen(false);
  };

  const handleDeleteClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    openDeleteTrapModal(trap.id, trap.serial_number, true);
  };

  return (
    <div className="trap-popup mw-100" style={{ width: '500px', position: 'relative' }}>
      <div className="trap-popup-header mb-2 fs-16 text-purple">
        <strong>
          {trapType === 'srst'
            ? t('Popups.SRSTPopup.title', { number: trap.number })
            : t('Popups.SMSTPopup.title', { number: trap.number })}
        </strong>
      </div>
      <div className="mb-2">{t('Popups.MctPopup.serialNumber', { slug: trap.slug })}</div>

      <div className="text-nowrap">{t('Popups.SMSTPopup.lastCapture')}</div>

      <div
        className="mb-1"
        onMouseEnter={() => {
          handleEnter();
          openModal(true);
        }}
        onMouseLeave={handleLeave}
      >
        <div className="text-nowrap">
          {trapType === 'srst'
            ? t('Popups.SRSTPopup.trapLastCapture', {
                num: 1,
                date: trap.last_capture_1 ? dayjs(trap.last_capture_1).format('DD.MM.YYYY HH:mm') : '-',
              })
            : t('Popups.SMSTPopup.trapLastCaptureOne', {
                num: 1,
                date: trap.last_capture_1 ? dayjs(trap.last_capture_1).format('DD.MM.YYYY HH:mm') : '-',
              })}
        </div>
        {trapType === 'smst' && (
          <div className="text-nowrap">
            {t('Popups.SMSTPopup.trapLastCaptureTwo', {
              num: 2,
              date: trap.last_capture_2 ? dayjs(trap.last_capture_2).format('DD.MM.YYYY HH:mm') : '-',
            })}
          </div>
        )}
      </div>

      <div className="text-nowrap border-1 border-top mt-2 pt-1">{t('Popups.SMSTPopup.lastCaptureWithoutCapture')}</div>

      <div
        className="mb-1"
        onMouseEnter={() => {
          handleEnter();
          openModal(false);
        }}
        onMouseLeave={handleLeave}
      >
        <div className="text-nowrap">
          {trapType === 'srst'
            ? t('Popups.SRSTPopup.trapLastCapture', {
                num: 1,
                date: trap.last_empty_1 ? dayjs(trap.last_empty_1).format('DD.MM.YYYY HH:mm') : '-',
              })
            : t('Popups.SMSTPopup.trapLastCaptureOne', {
                num: 1,
                date: trap.last_empty_1 ? dayjs(trap.last_empty_1).format('DD.MM.YYYY HH:mm') : '-',
              })}
        </div>
        {trapType === 'smst' && (
          <div className="text-nowrap">
            {t('Popups.SMSTPopup.trapLastCaptureTwo', {
              num: 2,
              date: trap.last_empty_2 ? dayjs(trap.last_empty_2).format('DD.MM.YYYY HH:mm') : '-',
            })}
          </div>
        )}
      </div>

      {trap.last_maintenance ? (
        <div className="text-nowrap mt-2">
          {t('Popups.MctPopup.lastMaintenance', {
            date: dayjs(trap.last_maintenance).format('DD.MM.YYYY HH:mm'),
          })}
        </div>
      ) : (
        <div className="text-success mt-2">{t('Popups.MctPopup.noMaintenance' as any)}</div>
      )}
      <div className="d-flex align-content-center justify-content-between">
        {/* Installment comment with file icon */}
        {trap.installment_comment && (
          <Popup
            trigger={
              <button className="btn btn-ghost-purple custom-toggle">
                <span className="icon-on">
                  <i className="ri-file-list-3-fill" />
                </span>
              </button>
            }
          >
            <div className="p-3 bg-white rounded-4">
              <strong>{t('Popups.MctPopup.installationComment')}</strong>
              <p className="text-muted fs-12 mt-2">{trap.installment_comment}</p>
            </div>
          </Popup>
        )}

        {/* Detail page with eye icon */}
        <div className="" title={t('Popups.MctPopup.viewTrapPage')}>
          <a href={`/r/trap/${trapType}/info/${trap.slug}`} className="btn btn-ghost-info custom-toggle">
            <span className="icon-on">
              <i className="ri-eye-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-eye-fill align-bottom" />
            </span>
          </a>
        </div>

        {/* Move action with drag icon */}
        <div className="" title={t('Popups.MctPopup.moveTrap')}>
          <button
            type="button"
            className="btn btn-ghost-success custom-toggle"
            data-bs-toggle="button"
            onClick={onMove}
          >
            <span className="icon-on">
              <i className="ri-drag-move-2-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-drag-move-2-fill align-bottom" />
            </span>
          </button>
        </div>

        {/* Delete trap with trash icon */}
        <div className="" title={t('Popups.MctPopup.removeTrap')}>
          <button
            type="button"
            id="del_trap_btn"
            className="btn btn-ghost-danger custom-toggle"
            data-bs-toggle="button"
            onClick={handleDeleteClick}
          >
            <span className="icon-on">
              <i className="ri-delete-bin-fill align-bottom" />
            </span>
            <span className="icon-off">
              <i className="ri-delete-bin-fill align-bottom" />
            </span>
          </button>
        </div>
      </div>
      {/* Action End Block */}

      {/* Move Actions Row */}
      {showMoveActions && (
        <div className="move-actions-row mt-2 text-center">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80px' }}>
              <ColorRing
                visible
                height="50"
                width="50"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
              />
            </div>
          ) : (
            <div className="d-grid gap-4 mt-4">
              <div className="row row-cols-2 g-2 align-items-stretch">
                <div className="col-4">
                  <button className="move_buttons" onClick={() => onMoveAction && onMoveAction('adjustment')}>
                    {t('Popups.MctPopup.adjustment')}
                  </button>
                </div>

                <div className="col col-8 d-flex flex-column justify-content-center align-items-start ps-3">
                  <div className="fw-bold">{t('Popups.MctPopup.minorMove')}</div>
                  <span className="text-start">{t('Popups.MctPopup.adjustmentSubtitle')}</span>
                </div>
              </div>

              <div className="row g-2 align-items-stretch">
                <div className="col-4">
                  <button className="move_buttons" onClick={() => onMoveAction && onMoveAction('new_place')}>
                    {t('Popups.MctPopup.newPlace')}
                  </button>
                </div>

                <div className="col col-8 d-flex flex-column justify-content-center align-items-start ps-3">
                  <div className="fw-bold">{t('Popups.MctPopup.newLocation')}</div>
                  <span className="text-start">{t('Popups.MctPopup.newLocationSubtitle')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {isModalOpen && (
        <CustomTooltip
          title={isCapture ? t('Popups.SMSTPopup.allCaptures') : t('Popups.SMSTPopup.allEmptyTriggers')}
          trapType={trapType}
          isOpen={isModalOpen}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          historyTriggers={dataForTooltip}
        />
      )}
    </div>
  );
};
